/**
 * RF-12.1 · Exportación del plan de mejoramiento.
 *
 * Produce el documento consolidado del plan institucional con sus acciones,
 * responsables, fechas y avance. Cubierto por `tests/planMejoramientoExport.spec.ts`.
 */

// RF-12.1 · Export del plan al formato institucional real:
// "PLAN DE MEJORAMIENTO Y FORTALECIMIENTO — GESTIÓN DE CALIDAD"
// HOJA 1 con los grupos de columnas del xlsx real (QUÉ · TIPO · PRIORIDAD ·
// QUIÉN · CUÁNDO · RECURSOS H/F/T · PRESUPUESTO · ENTREGABLE · FUENTE DE
// VERIFICACIÓN · SEGUIMIENTO Y CONTROL) + hoja de catálogos (Hoja1 real).
// Fuente: ESTRUCTURAS_REALES_INFORMES.md §5.

import ExcelJS from 'exceljs'
import type { PlanExport } from '@/services/PlanMejoramientoService'

const AZUL = 'FF0E69A0'

const COLORES_ESTADO: Record<string, string> = {
  NO_INICIADO: 'FFF5A623', // naranja
  EN_DESARROLLO: 'FFF8E71C', // amarillo
  CUMPLIDO: 'FF7ED321', // verde
  NO_CUMPLIDO: 'FFD0021B', // rojo
  ANULADO: 'FF4A90D9' // azul
}

function descargar(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nombre
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface ColExtra { titulo: string; filas: Record<string, string> }

/**
 * Genera y descarga el Excel del plan de mejoramiento institucional.
 *
 * La hoja fija las 8 primeras filas (`ySplit: 8`) para que los encabezados del
 * plan sigan visibles al desplazarse por las acciones, que suelen ser decenas.
 *
 * `columnasExtra` permite añadir columnas calculadas por la vista —por ejemplo el
 * seguimiento de un periodo concreto— sin que este módulo tenga que conocerlas:
 * cada una aporta su título y un mapa de valores por fila.
 *
 * Efecto lateral: dispara la descarga en el navegador.
 *
 * @param datos - Plan con sus acciones, responsables y avance.
 * @param columnasExtra - Columnas adicionales opcionales.
 */
export async function exportarPlanInstitucional(
  datos: PlanExport,
  columnasExtra: ColExtra[] = []
): Promise<void> {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('HOJA 1', { views: [{ state: 'frozen', ySplit: 8 }] })

  // ── Cabecera institucional ──
  ws.mergeCells('B1:N1')
  ws.getCell('B1').value = datos.cabecera.titulo
  ws.getCell('B1').font = { bold: true, size: 14 }
  ws.mergeCells('B3:N3')
  ws.getCell('B3').value = datos.cabecera.subtitulo
  ws.getCell('B3').font = { bold: true, size: 12 }
  ws.getCell('A5').value = 'FECHA DE ELABORACIÓN:'
  ws.getCell('B5').value = datos.cabecera.fecha_elaboracion ?? ''
  ws.getCell('F5').value = datos.cabecera.facultad ?? ''
  ws.getCell('I5').value = 'PROGRAMA:'
  ws.getCell('J5').value = datos.cabecera.programa ?? ''
  ws.getCell('L5').value = 'PERIODO:'
  ws.getCell('M5').value = datos.cabecera.periodo

  // ── Encabezados (2 filas, como el formato real) ──
  const fila7 = [
    'FACTOR', 'CARACTERÍSTICA CNA (Interno)', 'ASPECTO\n(INTERNO)', 'CAUSA RAIZ DE LA ACCIÓN',
    'QUE', '', 'TIPO DE ACCIÓN', 'PRIORIDAD', 'QUIÉN', 'CUÁNDO', '', 'RECURSOS', '', '',
    'PRESUPUESTO PROYECTADO', 'PRESUPUESTO EJECUTADO',
    'ENTREGABLE', '', '', '', '', '', '', '', '',
    'FUENTE DE VERIFICACIÓN (ENLACE http DE LA EVIDENCIA)',
    'SEGUIMIENTO Y CONTROL', '', '', '', ''
  ]
  const fila8 = [
    '', '', '', '',
    'ACTIVIDAD GENERAL', 'ACTIVIDADES ESPECIFICAS A REALIZAR', '', '',
    'CARGO DEL RESPONSABLE DE LA EJECUCIÓN',
    'FECHA DE INICIO\nDD/MM/AAAA', 'FECHA DE TERMINACIÓN\nDD/MM/AAAA',
    'H', 'F', 'T', '', '',
    'Documento', 'Listado', 'Encuesta', 'Estadística', 'Registro Fotográfico', 'Video', 'Grupo Focal', 'Otro', '¿Cuál?',
    '',
    'FECHA DE SEGUIMIENTO\nDD/MM/AAAA', 'ESTADO', '% AVANCE', 'FECHA DE CIERRE', 'OBSERVACIONES'
  ]
  ws.getRow(7).values = fila7
  ws.getRow(8).values = fila8

  const grupos: Array<[string, string]> = [
    ['E7', 'F7'], ['J7', 'K7'], ['L7', 'N7'], ['Q7', 'Y7'], ['AA7', 'AE7']
  ]
  for (const [ini, fin] of grupos) ws.mergeCells(`${ini}:${fin}`)
  // Solo se fusionan 7:8 las columnas cuya fila 8 está vacía. La I NO se fusiona:
  // lleva el grupo "QUIÉN" (fila 7) y la columna "CARGO DEL RESPONSABLE DE LA
  // EJECUCIÓN" (fila 8), como en el formato real (§5 del anexo).
  for (const col of ['A', 'B', 'C', 'D', 'G', 'H', 'O', 'P', 'Z']) ws.mergeCells(`${col}7:${col}8`)

  // Columnas extra del usuario: encabezado único (filas 7:8 fusionadas)
  columnasExtra.forEach((ec, i) => {
    ws.getRow(7).getCell(32 + i).value = ec.titulo
    ws.mergeCells(7, 32 + i, 8, 32 + i)
  })

  for (const fila of [7, 8]) {
    ws.getRow(fila).eachCell({ includeEmpty: false }, cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL } }
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 9 }
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' }
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
    })
  }

  // ── Filas de acciones ──
  const x = (v: unknown) => (v ? 'X' : '')
  let fila = 9
  for (const a of datos.acciones) {
    const rec = (a.recursos ?? {}) as Record<string, unknown>
    const ent = (a.entregables ?? {}) as Record<string, unknown>
    ws.getRow(fila).values = [
      a.factor ?? '', a.caracteristica ?? '', a.aspecto ?? '', a.causa_raiz ?? '',
      a.actividad_general ?? '', a.actividades_especificas ?? '',
      a.tipo ?? '', a.prioridad ?? '', a.responsable_cargo ?? '',
      a.fecha_inicio ?? '', a.fecha_terminacion ?? '',
      x(rec.h), x(rec.f), x(rec.t),
      a.presupuesto_proyectado ?? '', a.presupuesto_ejecutado ?? '',
      x(ent.documento), x(ent.listado), x(ent.encuesta), x(ent.estadistica),
      x(ent.registro_fotografico), x(ent.video), x(ent.grupo_focal), x(ent.otro), (ent.cual as string) ?? '',
      a.fuente_verificacion ?? '',
      a.fecha_seguimiento ?? '', String(a.estado ?? '').replace('_', ' '), a.avance_pct ?? 0,
      a.fecha_cierre ?? '', a.observaciones ?? ''
    ]
    // Columnas extra: mapeadas por id_accion (disponible en el export desde commit df02286)
    columnasExtra.forEach((ec, i) => {
      ws.getRow(fila).getCell(32 + i).value = a.id_accion != null ? (ec.filas[String(a.id_accion)] ?? '') : ''
    })
    const r = ws.getRow(fila)
    r.eachCell({ includeEmpty: true }, cell => {
      cell.alignment = { wrapText: true, vertical: 'top' }
      cell.font = { size: 9 }
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
    })
    // Semáforo institucional en la celda ESTADO (columna AB = 28).
    const color = COLORES_ESTADO[String(a.estado ?? '')]
    if (color) {
      r.getCell(28).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }
    }
    fila++
  }

  const anchos = [22, 30, 30, 28, 28, 32, 16, 11, 24, 13, 13, 4, 4, 4, 14, 14, 4, 4, 4, 4, 4, 4, 4, 4, 12, 34, 13, 15, 9, 13, 30]
  anchos.forEach((w, i) => { ws.getColumn(i + 1).width = w })
  columnasExtra.forEach((_, i) => { ws.getColumn(32 + i).width = 25 })

  // ── Hoja de catálogos (réplica de la Hoja1 real) ──
  const cat = wb.addWorksheet('Catálogos')
  cat.addRow(['TIPO DE ACCIÓN']).font = { bold: true }
  datos.catalogos.tipo_accion.forEach(t => cat.addRow([t]))
  cat.addRow([])
  cat.addRow(['PRIORIDAD']).font = { bold: true }
  datos.catalogos.prioridad.forEach(p => cat.addRow([p]))
  cat.addRow([])
  cat.addRow(['ESTADO', 'CONVENCIÓN']).font = { bold: true }
  for (const [estado, convencion] of Object.entries(datos.catalogos.estado)) {
    const r = cat.addRow([estado.replace('_', ' '), convencion.toUpperCase()])
    const color = COLORES_ESTADO[estado]
    if (color) r.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }
  }
  cat.getColumn(1).width = 20
  cat.getColumn(2).width = 14

  const buffer = await wb.xlsx.writeBuffer()
  const nombre = `Plan_Mejoramiento_y_Fortalecimiento_${datos.cabecera.programa ?? 'programa'}_${datos.cabecera.periodo}.xlsx`
  descargar(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nombre)
}
